import jamespot from 'jamespot-user-api';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { JiaTextEnhanceQuery } from '../../../hooks/useAI';

const JRCLoader = commonComponentsRegistry.getLazyComponent('Loader');
const JRCModalContent = commonComponentsRegistry.getLazyComponent('ModalContent');
const JRCModalLayout = commonComponentsRegistry.getLazyComponent('ModalLayout');
const JRCTypography = commonComponentsRegistry.getLazyComponent('Typography');

const TextWrapper = styled.div`
    padding: ${(props) => props.theme.space.md}px;
`;

export type AIModalProps = {
    query: JiaTextEnhanceQuery | undefined;
};

type ResponseType = {
    response: string | undefined;
    loading: boolean;
    error: string | undefined;
};

const loadingResponse: ResponseType = {
    response: undefined,
    loading: true,
    error: undefined,
};

export const AIModal = ({ query }: AIModalProps) => {
    const intl = useIntl();
    const [response, setResponse] = useState<ResponseType>(loadingResponse);
    const theme = useTheme();

    const sendAIRequest = useCallback(
        (query: JiaTextEnhanceQuery) => {
            setResponse(loadingResponse);
            jamespot.jia
                .textEnhancement({ text: query.text, capacity: query.capability })
                .then((response) => {
                    if (response.error) {
                        setResponse({
                            error: `${intl.formatMessage({ id: 'JIA_ERROR_AI_CALL' })}${
                                response.errorMsg && ': ' + response.errorMsg
                            }`,
                            loading: false,
                            response: undefined,
                        });
                        return;
                    }
                    const textResponse = response.result.text;
                    setResponse({
                        error: undefined,
                        loading: false,
                        response: textResponse,
                    });
                })
                .catch((_err) => {
                    setResponse({
                        error: intl.formatMessage({ id: 'JIA_ERROR_AI_CALL' }),
                        loading: false,
                        response: undefined,
                    });
                });
        },
        [intl],
    );

    useEffect(() => {
        if (query && query.text.trim().length > 0) {
            sendAIRequest(query);
        }
    }, [query, sendAIRequest]);

    return (
        <JRCModalLayout
            zIndex={theme.zIndex.tinyMCEModal}
            open={!!query}
            closeHandler={() => {
                query?.reject();
            }}>
            <JRCModalContent
                overflow="scroll"
                buttons={[
                    {
                        children: intl.formatMessage({ id: 'GLOBAL_Try_Again' }),
                        clickHandler: () => {
                            query && sendAIRequest(query);
                        },
                    },
                    {
                        children: intl.formatMessage({ id: 'GLOBAL_Validation' }),
                        clickHandler: () => response.response && query?.resolve(response.response),
                    },
                    {
                        children: intl.formatMessage({ id: 'GLOBAL_Cancel' }),
                        clickHandler: () => query?.reject(),
                    },
                ]}
                isFull>
                <TextWrapper>
                    {response.loading && (
                        <>
                            <JRCLoader />
                            <JRCTypography align="center">
                                {intl.formatMessage({ id: 'JIA_WAITING_WARNIG' })}
                            </JRCTypography>
                        </>
                    )}
                    {!response.loading && response.error && <p>{response.error}</p>}
                    {!response.loading && response.response && <p>{response.response}</p>}
                </TextWrapper>
            </JRCModalContent>
        </JRCModalLayout>
    );
};
